import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";

import { ReportSectionFieldsType, ReportSectionsType } from "@api/graphql/types-and-hooks";
import { ReportFieldDropdownMenuMoveToAppendix } from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuMoveToAppendix";
import { ReportFieldDropdownMenuMoveToSection } from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuMoveToSection";
import {
  ReportFieldDropdownMenuRemoveDialog,
  ReportFieldDropdownMenuRemoveItem,
} from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuRemove";
import { ReportFieldDropdownMenuReorder } from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuReorder";
import { ReportFieldDropdownMenuReturnFromAppendix } from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuReturnFromAppendix";
import {
  ReportFieldDropdownMenuSaveToFolderDialog,
  ReportFieldDropdownMenuSaveToFolderItem,
} from "@components/ReportField/ReportFieldDropdownMenu/ReportFieldDropdownMenuSaveToFolder";
import { RevealOnFieldHover } from "@components/ReportField/RevealOnFieldHover";
import {
  useCanDeleteField,
  useCanMoveToAppendix,
  useCanMoveToSection,
  useCanReorderField,
  useCanReturnFromAppendix,
  useCanSaveToFolder,
} from "@components/ReportField/capabilities";

export interface ReportFieldDropdownMenuProps {
  section: ReportSectionsType;
  field: ReportSectionFieldsType;
}

export const ReportFieldDropdownMenu: React.FC<ReportFieldDropdownMenuProps> = ({ field, section }) => {
  const [isSaveToFolderOpen, setIsSaveToFolderOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const canSaveToFolder = useCanSaveToFolder(section);
  const canReorder = useCanReorderField(section);
  const canMoveToSection = useCanMoveToSection(section);
  const canMoveToAppendix = useCanMoveToAppendix(section);
  const canReturnFromAppendix = useCanReturnFromAppendix(section);
  const canDelete = useCanDeleteField(section);

  const canReorderOrMoveToSection = canReorder || canMoveToSection;
  const canMoveToOrFromAppendix = canMoveToAppendix || canReturnFromAppendix;
  const hasItemsAboveRemove = canSaveToFolder || canReorderOrMoveToSection || canMoveToOrFromAppendix;
  const showActionMenu = hasItemsAboveRemove || canDelete;

  if (!showActionMenu) return null;

  return (
    <>
      <RevealOnFieldHover>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="outline" size="icon-xs" aria-label="More actions">
                <EllipsisVerticalIcon />
              </Button>
            }
          />
          <DropdownMenuContent className="min-w-48" align="end">
            <DropdownMenuGroup>
              {canSaveToFolder && (
                <ReportFieldDropdownMenuSaveToFolderItem onOpen={() => setIsSaveToFolderOpen(true)} />
              )}
              {canReorderOrMoveToSection && (
                <>
                  <DropdownMenuLabel>Reorder</DropdownMenuLabel>
                  {canReorder && <ReportFieldDropdownMenuReorder section={section} field={field} />}
                  {canMoveToSection && <ReportFieldDropdownMenuMoveToSection section={section} field={field} />}
                </>
              )}
              {canMoveToAppendix && <ReportFieldDropdownMenuMoveToAppendix section={section} field={field} />}
              {canReturnFromAppendix && <ReportFieldDropdownMenuReturnFromAppendix section={section} field={field} />}
            </DropdownMenuGroup>
            {canDelete && (
              <>
                {hasItemsAboveRemove && <DropdownMenuSeparator />}
                <ReportFieldDropdownMenuRemoveItem onOpen={() => setIsDeleteOpen(true)} />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </RevealOnFieldHover>
      {canSaveToFolder && (
        <ReportFieldDropdownMenuSaveToFolderDialog
          field={field}
          open={isSaveToFolderOpen}
          onOpenChange={setIsSaveToFolderOpen}
        />
      )}
      {canDelete && (
        <ReportFieldDropdownMenuRemoveDialog
          section={section}
          field={field}
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
        />
      )}
    </>
  );
};
